import { Form } from '$components'
import { z } from 'zod'
import { createContext, useContext, ReactNode } from 'react'
import { CreateSizeCategoryFormContent } from './content'
import { CreateSizeCategoryFormControls } from './controls'
import { FieldErrors } from 'react-hook-form'

export enum Input {
  Name = 'name',
  FromWeightLbs = 'from_weight_lbs',
  ToWeightLbs = 'to_weight_lbs',
}

export const createSizeCategorySchema = z.object({
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
  [Input.FromWeightLbs]: z.coerce.number({ invalid_type_error: 'From weight is required.' })
    .min(0, { message: 'From weight must be 0 or greater.' }),
  [Input.ToWeightLbs]: z.coerce.number({ invalid_type_error: 'To weight is required.' })
    .min(0, { message: 'To weight must be 0 or greater.' }),
}).refine((data) => data[Input.ToWeightLbs] > data[Input.FromWeightLbs], {
  message: 'To weight must be greater than from weight.',
  path: [Input.ToWeightLbs],
})

export type CreateSizeCategorySchema = z.infer<typeof createSizeCategorySchema>

type CreateSizeCategoryFormContextType = {
  onSubmit: (values: CreateSizeCategorySchema) => void
  onCancel: () => void
  isLoading?: boolean
  errors?: FieldErrors<CreateSizeCategorySchema>
}

const CreateSizeCategoryFormContext = createContext<CreateSizeCategoryFormContextType | undefined>(undefined)

type CreateSizeCategoryFormProps = CreateSizeCategoryFormContextType & {
  children?: ReactNode
}

export function CreateSizeCategoryForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  errors,
}: CreateSizeCategoryFormProps) {
  const context: CreateSizeCategoryFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
    errors,
  }

  return (
    <CreateSizeCategoryFormContext.Provider value={context}>
      <Form<CreateSizeCategorySchema, typeof createSizeCategorySchema>
        schema={createSizeCategorySchema}
        onSubmit={onSubmit}
        errors={errors}
      >
        {children}
      </Form>
    </CreateSizeCategoryFormContext.Provider>
  )
}

export function useCreateSizeCategoryForm() {
  const context = useContext(CreateSizeCategoryFormContext)
  if (context === undefined) {
    throw new Error('useCreateSizeCategoryForm must be used within a CreateSizeCategoryForm')
  }
  return context
}

CreateSizeCategoryForm.Content = CreateSizeCategoryFormContent
CreateSizeCategoryForm.Controls = CreateSizeCategoryFormControls
